import { useLocation } from 'react-router-dom';

import { KycStates } from '@ha/api/v2/types/Kyc';
import {
  MenuList,
  ListItemText,
  ListItemIcon,
  Badge,
  Collapse,
  Kind,
} from '@ha/dsl';
import {
  LayoutDashboardLight,
  StarLight,
  MessagesBubbleSquareTyping1Light,
  LoginKeysLight,
  AdvertisingBillboardHouseLight,
  CashPaymentCoinsLight,
  ArrowButtonUpBold,
  ArrowButtonDownBold,
} from '@ha/icons';
import { makeStyles } from 'tss-react/mui';
import { MenuItem } from 'ha/components/Menu/MenuItem';
import { SmartTrack as Track } from 'ha/components/track/SmartTrack';
import { useIntl } from 'ha/i18n';
import React from 'react';

import { NavBarPayoutListItems } from './NavBarPayoutListItems';

const useStyles = makeStyles()(theme => ({
  sidebar: {
    width: theme.spacing(31),
    paddingBlock: theme.spacing(2.5),
    position: 'relative',

    '& .MuiListItemIcon-root': {
      minWidth: 'unset !important',
      paddingRight: theme.spacing(1.5),
    },
    '& .MuiSvgIcon-root': {
      fontSize: '1rem',
      color: theme.palette.grey[700],
    },

    '& .MuiMenuItem-root': {
      '& .MuiListItemText-primary': {
        color: theme.palette.grey[600],
      },
    },

    '& .MuiMenuItem-root.Mui-selected': {
      '& .MuiListItemText-primary': {
        fontWeight: theme.typography.fontWeightBold,
      },
    },

    '& .MuiCollapse-root .MuiMenuItem-root': {
      paddingLeft: theme.spacing(6.5),
      paddingBlock: theme.spacing(0.5),

      '& .MuiListItemText-primary': {
        fontSize: theme.typography.body2.fontSize,
      },
    },
  },

  sidebarContent: {
    position: 'sticky',
    top: theme.spacing(2.5),
  },
  arrowIcon: {
    fontSize: '0.75rem !important',
    color: theme.palette.grey[700],
  },
  inboxItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  payoutsItem: {
    backgroundColor: 'transparent !important',
  },
}));

interface Props {
  kycStatus: KycStates | undefined;
  unreadMessageCount: number;
}

export function NavBarSide({ kycStatus, unreadMessageCount }: Props) {
  const { classes } = useStyles();

  const { pathname } = useLocation();

  const { T, urlResolver } = useIntl();

  const payoutsSubroutes = React.useMemo(
    () => [
      urlResolver.getPayoutMethodsUrl(),
      urlResolver.getBillingUrl(),
      urlResolver.getTransactionHistoryUrl(),
      urlResolver.getPayoutsPaymentRequests(),
      urlResolver.getInvoicesUrl(),
      urlResolver.getCommissionPlanUrl(),
      urlResolver.getVerifyIdentityUrl(),
    ],
    [urlResolver],
  );

  const [paymentsExpanded, setPaymentsExpanded] = React.useState(() =>
    payoutsSubroutes.includes(pathname),
  );

  const togglePayments = React.useCallback(() => {
    setPaymentsExpanded(previous => !previous);
  }, []);

  return (
    <div className={classes.sidebar}>
      <div className={classes.sidebarContent}>
        <MenuList>
          <Track name="Clicked Dashboard in navigation">
            <MenuItem to={urlResolver.getDashboardUrl()}>
              <ListItemIcon>
                <LayoutDashboardLight />
              </ListItemIcon>
              <ListItemText primary={T('dashboard.page_title')} />
            </MenuItem>
          </Track>

          <Track name="Clicked Inbox in navigation">
            <MenuItem to={urlResolver.getMyInboxUrl()}>
              <ListItemIcon>
                <MessagesBubbleSquareTyping1Light />
              </ListItemIcon>
              <ListItemText primary={T('inbox.page_title')} />

              {unreadMessageCount > 0 && (
                <Badge
                  data-test-locator="NavBarSide - InboxCounters"
                  kind={Kind.inline}
                  color={
                    urlResolver.getMyInboxUrl() === pathname
                      ? 'secondary'
                      : 'grey'
                  }
                  badgeContent={unreadMessageCount}
                />
              )}
            </MenuItem>
          </Track>

          <Track name="Clicked Listings in navigation">
            <MenuItem to={urlResolver.getMyListingsUrl()}>
              <ListItemIcon>
                <AdvertisingBillboardHouseLight />
              </ListItemIcon>
              <ListItemText primary={T('listings.page_title')} />
            </MenuItem>
          </Track>

          <Track name="Clicked Bookings in navigation">
            <MenuItem to={urlResolver.getBookingsUrl()}>
              <ListItemIcon>
                <LoginKeysLight />
              </ListItemIcon>
              <ListItemText primary={T('bookings.page_title')} />
            </MenuItem>
          </Track>

          <Track name="Clicked Payments in navigation (expanding the menu)">
            <MenuItem onClick={togglePayments} className={classes.payoutsItem}>
              <ListItemIcon>
                <CashPaymentCoinsLight />
              </ListItemIcon>
              <ListItemText primary={T('payments.page_title')} />

              {paymentsExpanded ? (
                <ArrowButtonUpBold className={classes.arrowIcon} />
              ) : (
                <ArrowButtonDownBold className={classes.arrowIcon} />
              )}
            </MenuItem>
          </Track>

          <Collapse in={paymentsExpanded} timeout="auto">
            <MenuList disablePadding>
              <NavBarPayoutListItems kycStatus={kycStatus} />
            </MenuList>
          </Collapse>

          <Track name="Clicked Reviews in navigation">
            <MenuItem to={urlResolver.getMyTenantReviewsDashboard()}>
              <ListItemIcon>
                <StarLight />
              </ListItemIcon>
              <ListItemText primary={T('tenant_reviews.page_title')} />
            </MenuItem>
          </Track>
        </MenuList>
      </div>
    </div>
  );
}
